import { lazy } from "react";
import { Spinner, ToastProvider } from "@constructorfleet/ui";
import { Navigate, Route, Routes } from "react-router-dom";
import { ProductCapability } from "@constructorfleet/offerings-sdk";
import { AppLayout } from "./components/app-layout";
import { ProtectedRoute } from "./components/protected-route";
import { offeringHasCapability } from "./offerings/capabilities";
import { browserProductDbClient } from "./offerings/db-client";
import { useOfferings } from "./offerings/use-offerings";

const HomePage = lazy(() =>
  import("./pages/home-page").then((module) => ({ default: module.HomePage }))
);
const LoginPage = lazy(() =>
  import("./pages/login-page").then((module) => ({ default: module.LoginPage }))
);
const VerifyPage = lazy(() =>
  import("./pages/verify-page").then((module) => ({ default: module.VerifyPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/forgot-password-page").then((module) => ({
    default: module.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/reset-password-page").then((module) => ({ default: module.ResetPasswordPage }))
);
const AboutPage = lazy(() =>
  import("./pages/about-page").then((module) => ({ default: module.AboutPage }))
);
const NotFoundPage = lazy(() =>
  import("./pages/not-found-page").then((module) => ({ default: module.NotFoundPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/privacy-policy-page").then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);
const ProductDashboardPage = lazy(() =>
  import("./pages/product-dashboard-page").then((module) => ({
    default: module.ProductDashboardPage,
  }))
);
const PricingPage = lazy(() =>
  import("./pages/pricing-page").then((module) => ({ default: module.PricingPage }))
);
const RefundPolicyPage = lazy(() =>
  import("./pages/refund-policy-page").then((module) => ({
    default: module.RefundPolicyPage,
  }))
);
const SettingsPage = lazy(() =>
  import("./pages/settings-page").then((module) => ({ default: module.SettingsPage }))
);
const ApiAccessPage = lazy(() =>
  import("./pages/api-access-page").then((module) => ({ default: module.ApiAccessPage }))
);
const ApiDocsPage = lazy(() =>
  import("./pages/api-docs-page").then((module) => ({ default: module.ApiDocsPage }))
);
const TermsPage = lazy(() =>
  import("./pages/terms-page").then((module) => ({ default: module.TermsPage }))
);
const DesignSystemPage = lazy(() =>
  import("./pages/ui-kit-page").then((module) => ({ default: module.UiKitPage }))
);
const UserDashboardPage = lazy(() =>
  import("./pages/user-dashboard-page").then((module) => ({ default: module.UserDashboardPage }))
);
const AdminDashboardPage = lazy(() =>
  import("./pages/admin-dashboard-page").then((module) => ({
    default: module.AdminDashboardPage,
  }))
);
const AdminUsersPage = lazy(() =>
  import("./pages/admin-users-page").then((module) => ({ default: module.AdminUsersPage }))
);
const AdminFeedbackPage = lazy(() =>
  import("./pages/admin-feedback-page").then((module) => ({
    default: module.AdminFeedbackPage,
  }))
);
const AdminAnalyticsPage = lazy(() =>
  import("./pages/admin-analytics-page").then((module) => ({
    default: module.AdminAnalyticsPage,
  }))
);
const AdminExperimentsPage = lazy(() =>
  import("./pages/admin-experiments-page").then((module) => ({
    default: module.AdminExperimentsPage,
  }))
);
const AdminUaDirectoryPage = lazy(() =>
  import("./pages/admin-ua-directory-page").then((module) => ({
    default: module.AdminUaDirectoryPage,
  }))
);

function App() {
  const { offerings, isLoading } = useOfferings();

  if (isLoading) {
    return (
      <div className="cf-loading-block">
        <Spinner size="sm" />
        Loading...
      </div>
    );
  }

  return (
    <ToastProvider>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<HomePage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="verify" element={<VerifyPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          {offerings
            .filter((offering) => offeringHasCapability(offering, ProductCapability.Dashboard))
            .map((offering) => (
              <Route
                key={`${offering.id}-product`}
                path={offering.routes.productPath}
                element={offering.productPage(browserProductDbClient)}
              />
            ))}
          {offerings
            .filter((offering) => Boolean(offering.routes.signupPath && offering.signupPage))
            .map((offering) => (
              <Route
                key={`${offering.id}-signup`}
                path={offering.routes.signupPath}
                element={offering.signupPage?.(browserProductDbClient)}
              />
            ))}
          {offerings
            .filter((offering) => Boolean(offering.routes.demoPath && offering.demoPage))
            .map((offering) => (
              <Route
                key={`${offering.id}-demo`}
                path={offering.routes.demoPath}
                element={offering.demoPage?.(browserProductDbClient)}
              />
            ))}
          {offerings.flatMap((offering) =>
            (offering.addons ?? [])
              .filter((addon) => addon.routes.detailPath)
              .map((addon) => (
                <Route
                  key={`${addon.id}-detail`}
                  path={addon.routes.detailPath}
                  element={addon.detailPage(browserProductDbClient)}
                />
              ))
          )}

          <Route element={<ProtectedRoute allowedRoles={["user", "admin"]} />}>
            <Route path="dashboard" element={<UserDashboardPage />} />
            <Route path="dashboard/:productId" element={<ProductDashboardPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/api-access" element={<ApiAccessPage />} />
            <Route path="settings/api-docs" element={<ApiDocsPage />} />
            <Route path="billing" element={<Navigate to="/dashboard" replace />} />
            {offerings.flatMap((offering) =>
              (offering.addons ?? [])
                .filter((addon) => addon.routes.settingsPath && addon.settingsPage)
                .map((addon) => (
                  <Route
                    key={`${addon.id}-settings`}
                    path={addon.routes.settingsPath}
                    element={addon.settingsPage!(browserProductDbClient)}
                  />
                ))
            )}
          </Route>

          <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
            <Route path="admin" element={<AdminDashboardPage />} />
            {offerings
              .filter((offering) =>
                offeringHasCapability(offering, ProductCapability.AdminSettings)
              )
              .map((offering) => (
                <Route
                  key={`${offering.id}-admin-settings`}
                  path={offering.routes.adminSettingsPath}
                  element={offering.adminUI(browserProductDbClient)}
                />
              ))}
            <Route path="admin/users" element={<AdminUsersPage />} />
            <Route path="admin/feedback" element={<AdminFeedbackPage />} />
            <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="admin/experiments" element={<AdminExperimentsPage />} />
            <Route path="admin/ua-directory" element={<AdminUaDirectoryPage />} />
            <Route path="ui-kit" element={<DesignSystemPage />} />
          </Route>

          <Route path="about" element={<AboutPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="refunds" element={<RefundPolicyPage />} />

          <Route path="home" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ToastProvider>
  );
}

export default App;
